"use client";

import { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Upload,
  Save,
  Plus,
  Loader2,
  AlertCircle,
  CheckCircle,
  Trash2,
} from "lucide-react";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { SHAREPOINT_FY_YEAR } from "@/constants/sharepoint-config";

interface AuditEntry {
  book_name: string;
  ledger_id: number;
  ledger_name: string;
  amount: number;
  management_remarks: string;
  "ET Remarks": string;
  note_line: string;
}

interface AuditEntriesData {
  "Audit Entries": {
    [entryNumber: string]: AuditEntry[];
  };
}

interface AuditEntriesSectionProps {
  searchTerm?: string;
}

export function AuditEntriesSection({ searchTerm = "" }: AuditEntriesSectionProps) {
  const { isArchivalMode } = useArchivalMode();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditEntriesData>({
    "Audit Entries": {},
  });
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  const [unsavedEntryNumbers, setUnsavedEntryNumbers] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load audit entries on mount
  useEffect(() => {
    loadAuditEntries();
  }, []);

  const loadAuditEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await (window as any).sharePointAPI.downloadAuditEntries();
      if (result.success && result.data) {
        setAuditEntries(result.data);
        // Expand all entries by default
        const allEntryNumbers = Object.keys(result.data["Audit Entries"] || {});
        setExpandedEntries(new Set(allEntryNumbers));
        // All loaded entries are considered "saved" (not new)
        setUnsavedEntryNumbers(new Set());
      } else {
        setAuditEntries({ "Audit Entries": {} });
        setUnsavedEntryNumbers(new Set());
      }
    } catch (err: any) {
      setError(`Failed to load audit entries: ${err.message}`);
      setAuditEntries({ "Audit Entries": {} });
    } finally {
      setLoading(false);
    }
  };

  const saveAuditEntries = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Validate all entries have zero sum
      const validationErrors: string[] = [];
      for (const [entryNumber, entries] of Object.entries(auditEntries["Audit Entries"])) {
        const sum = entries.reduce((acc, entry) => acc + (entry.amount || 0), 0);
        if (Math.abs(sum) > 0.01) { // Allow small floating point errors
          validationErrors.push(`Entry ${entryNumber}: Amounts sum to ${sum.toFixed(2)}, must be zero`);
        }
      }

      if (validationErrors.length > 0) {
        setError(`Validation failed:\n${validationErrors.join("\n")}`);
        setSaving(false);
        return;
      }

      const result = await (window as any).sharePointAPI.saveAuditEntries(auditEntries);
      if (result.success) {
        setSuccess("Audit entries saved successfully!");
        // Clear unsaved entries - all entries are now saved
        setUnsavedEntryNumbers(new Set());
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || "Failed to save audit entries");
      }
    } catch (err: any) {
      setError(`Failed to save audit entries: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      // Step 1: Read file as base64 (browser-safe approach; actual clone issue
      // was fixed on the IPC result side, so this is fine and more portable)
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          if (!result) {
            reject(new Error("Failed to read file - empty result"));
            return;
          }
          resolve(result);
        };
        reader.onerror = () => {
          reject(new Error("Failed to read file"));
        };
        reader.readAsDataURL(file);
      });

      // Extract base64 data (remove data URL prefix)
      const base64Data = base64Content.includes(",") 
        ? base64Content.split(",")[1] 
        : base64Content;

      if (!base64Data || base64Data.trim().length === 0) {
        throw new Error("Failed to extract base64 data from file - file may be empty or corrupted");
      }

      console.log("File read successfully, base64 length:", base64Data.length);

      // Step 2: Upload file to SharePoint juggernaut folder first
      console.log("Uploading Excel file to SharePoint juggernaut folder:", file.name, "Size:", file.size);
      const uploadResult = await (window as any).sharePointAPI.uploadFile({
        fileName: file.name,
        fileContent: base64Data,
        folderName: "juggernaut",
        fyYear: String(SHAREPOINT_FY_YEAR),
        referenceValue: "Audit Entries Excel Upload",
      });

      console.log("Upload result:", uploadResult);

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || "Failed to upload file to SharePoint");
      }

      console.log("File uploaded to SharePoint successfully, waiting a moment before processing:", file.name);

      // Small delay to ensure file is fully available in SharePoint
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 3: Process the file from SharePoint
      console.log("Starting to process file from SharePoint:", file.name);
      const result = await (window as any).sharePointAPI.uploadAuditEntriesExcel({
        fileName: file.name,
      });

      console.log("Processing result:", result);

          if (result.success) {
            setSuccess(`Excel file processed successfully! ${result.message || ""}`);
            // Reload audit entries (all will be marked as saved)
            await loadAuditEntries();
            setTimeout(() => setSuccess(null), 5000);
          } else {
            setError(result.error || "Failed to process Excel file");
          }
    } catch (err: any) {
      console.error("Error uploading/processing file:", err);
      setError(`Failed to process Excel file: ${err.message || "Unknown error"}`);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const toggleEntry = (entryNumber: string) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryNumber)) {
      newExpanded.delete(entryNumber);
    } else {
      newExpanded.add(entryNumber);
    }
    setExpandedEntries(newExpanded);
  };

  const updateEntry = (
    entryNumber: string,
    index: number,
    field: keyof AuditEntry,
    value: string | number
  ) => {
    const newEntries = { ...auditEntries };
    const entryList = [...(newEntries["Audit Entries"][entryNumber] || [])];
    entryList[index] = { ...entryList[index], [field]: value };
    newEntries["Audit Entries"][entryNumber] = entryList;
    setAuditEntries(newEntries);
  };

  const addNewEntry = () => {
    const newEntryNumber = `ENTRY_${Date.now()}`;
    const newEntries = { ...auditEntries };
    newEntries["Audit Entries"][newEntryNumber] = [
      {
        book_name: "",
        ledger_id: 0,
        ledger_name: "",
        amount: 0,
        management_remarks: "",
        "ET Remarks": "",
        note_line: "",
      },
      {
        book_name: "",
        ledger_id: 0,
        ledger_name: "",
        amount: 0,
        management_remarks: "",
        "ET Remarks": "",
        note_line: "",
      },
    ];
    setAuditEntries(newEntries);
    setExpandedEntries(new Set([...expandedEntries, newEntryNumber]));
    // Mark this entry as unsaved (new)
    setUnsavedEntryNumbers(new Set([...unsavedEntryNumbers, newEntryNumber]));
  };

  const addRowToEntry = (entryNumber: string) => {
    const newEntries = { ...auditEntries };
    const entryList = [...(newEntries["Audit Entries"][entryNumber] || [])];
    entryList.push({
      book_name: entryList[0]?.book_name || "",
      ledger_id: 0,
      ledger_name: "",
      amount: 0,
      management_remarks: "",
      "ET Remarks": "",
      note_line: "",
    });
    newEntries["Audit Entries"][entryNumber] = entryList;
    setAuditEntries(newEntries);
  };

  const removeRowFromEntry = (entryNumber: string, index: number) => {
    const newEntries = { ...auditEntries };
    const entryList = [...(newEntries["Audit Entries"][entryNumber] || [])];
    if (entryList.length > 1) {
      entryList.splice(index, 1);
      newEntries["Audit Entries"][entryNumber] = entryList;
      setAuditEntries(newEntries);
    }
  };

  const deleteEntry = (entryNumber: string) => {
    const newEntries = { ...auditEntries };
    delete newEntries["Audit Entries"][entryNumber];
    setAuditEntries(newEntries);
    const newExpanded = new Set(expandedEntries);
    newExpanded.delete(entryNumber);
    setExpandedEntries(newExpanded);
  };

  const calculateEntrySum = (entries: AuditEntry[]) => {
    return entries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
  };

  const entryNumbers = Object.keys(auditEntries["Audit Entries"] || {}).sort();

  // Filter entries based on search term
  const filteredEntryNumbers = entryNumbers.filter((entryNumber) => {
    if (!searchTerm) return true;
    const entries = auditEntries["Audit Entries"][entryNumber] || [];
    const searchLower = searchTerm.toLowerCase();
    return (
      entryNumber.toLowerCase().includes(searchLower) ||
      entries.some(
        (entry) =>
          entry.book_name?.toLowerCase().includes(searchLower) ||
          entry.ledger_name?.toLowerCase().includes(searchLower) ||
          entry.management_remarks?.toLowerCase().includes(searchLower) ||
          entry["ET Remarks"]?.toLowerCase().includes(searchLower)
      )
    );
  });

  return (
    <div className="flex h-full flex-col space-y-4 p-6">
      <Card className="border-white/10 bg-black/40 backdrop-blur-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold text-white">
                Audit Entries
              </CardTitle>
              <CardDescription className="text-white/60">
                Upload and manage audit entries for trial balance adjustments
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="audit-entries-upload"
                disabled={isArchivalMode}
              />
              <Button
                variant="outline"
                className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                disabled={isArchivalMode || uploading || loading}
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.click();
                  }
                }}
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Excel
                  </>
                )}
              </Button>
              <Button
                onClick={addNewEntry}
                variant="outline"
                className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                disabled={isArchivalMode || loading}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Entry
              </Button>
              <Button
                onClick={saveAuditEntries}
                variant="default"
                className="bg-blue-600 text-white hover:bg-blue-700"
                disabled={isArchivalMode || saving || loading}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-red-400">
              <AlertCircle className="h-5 w-5" />
              <pre className="whitespace-pre-wrap text-sm">{error}</pre>
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-500/50 bg-green-500/10 p-3 text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm">{success}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : filteredEntryNumbers.length === 0 ? (
            <div className="py-12 text-center text-white/60">
              No audit entries found. Upload an Excel file or add a new entry to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {filteredEntryNumbers.map((entryNumber) => {
                const entries = auditEntries["Audit Entries"][entryNumber] || [];
                const sum = calculateEntrySum(entries);
                const isExpanded = expandedEntries.has(entryNumber);
                const isValid = Math.abs(sum) < 0.01;

                return (
                  <Card
                    key={entryNumber}
                    className="border-white/10 bg-black/20 backdrop-blur-sm"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleEntry(entryNumber)}
                            className="text-white hover:bg-white/10"
                          >
                            {isExpanded ? "▼" : "▶"} {entryNumber}
                          </Button>
                          <Badge
                            variant={isValid ? "default" : "destructive"}
                            className={
                              isValid
                                ? "bg-green-500/20 text-green-400 border-green-500/50"
                                : "bg-red-500/20 text-red-400 border-red-500/50"
                            }
                          >
                            {isValid ? "Valid" : `Sum: ${sum.toFixed(2)}`}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => addRowToEntry(entryNumber)}
                            className="text-white hover:bg-white/10"
                            disabled={isArchivalMode}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteEntry(entryNumber)}
                            className="text-red-400 hover:bg-red-500/10"
                            disabled={isArchivalMode}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    {isExpanded && (
                      <CardContent>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-white/10">
                                <TableHead className="text-white/80">Book Name</TableHead>
                                <TableHead className="text-white/80">Ledger ID</TableHead>
                                <TableHead className="text-white/80">Ledger Name</TableHead>
                                <TableHead className="text-white/80">Amount</TableHead>
                                <TableHead className="text-white/80">Management Remarks</TableHead>
                                <TableHead className="text-white/80">ET Remarks</TableHead>
                                <TableHead className="text-white/80">Note Line</TableHead>
                                <TableHead className="text-white/80">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {entries.map((entry, index) => {
                                // Determine if this entry is unsaved (newly added, not yet saved)
                                const isUnsaved = unsavedEntryNumbers.has(entryNumber);
                                
                                return (
                                <TableRow
                                  key={index}
                                  className="border-white/10 hover:bg-white/5"
                                >
                                  <TableCell className="text-white/90">
                                    <Input
                                      value={entry.book_name}
                                      readOnly={!isUnsaved}
                                      onChange={(e) =>
                                        isUnsaved &&
                                        updateEntry(
                                          entryNumber,
                                          index,
                                          "book_name",
                                          e.target.value
                                        )
                                      }
                                      className="bg-black/40 text-white"
                                      placeholder={isUnsaved ? "Enter book name" : ""}
                                    />
                                  </TableCell>
                                  <TableCell className="text-white/90">
                                    <Input
                                      type="number"
                                      value={entry.ledger_id}
                                      onChange={(e) =>
                                        updateEntry(
                                          entryNumber,
                                          index,
                                          "ledger_id",
                                          parseInt(e.target.value) || 0
                                        )
                                      }
                                      className="bg-black/40 text-white"
                                    />
                                  </TableCell>
                                  <TableCell className="text-white/90">
                                    <Input
                                      value={entry.ledger_name}
                                      onChange={(e) =>
                                        updateEntry(
                                          entryNumber,
                                          index,
                                          "ledger_name",
                                          e.target.value
                                        )
                                      }
                                      className="bg-black/40 text-white"
                                    />
                                  </TableCell>
                                  <TableCell className="text-white/90">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={entry.amount}
                                      onChange={(e) =>
                                        updateEntry(
                                          entryNumber,
                                          index,
                                          "amount",
                                          parseFloat(e.target.value) || 0
                                        )
                                      }
                                      className="bg-black/40 text-white"
                                    />
                                  </TableCell>
                                  <TableCell className="text-white/90">
                                    <Input
                                      value={entry.management_remarks}
                                      onChange={(e) =>
                                        updateEntry(
                                          entryNumber,
                                          index,
                                          "management_remarks",
                                          e.target.value
                                        )
                                      }
                                      className="bg-black/40 text-white"
                                    />
                                  </TableCell>
                                  <TableCell className="text-white/90">
                                    <Input
                                      value={entry["ET Remarks"]}
                                      onChange={(e) =>
                                        updateEntry(
                                          entryNumber,
                                          index,
                                          "ET Remarks",
                                          e.target.value
                                        )
                                      }
                                      className="bg-black/40 text-white"
                                    />
                                  </TableCell>
                                  <TableCell className="text-white/90">
                                    <Input
                                      value={entry.note_line}
                                      readOnly={!isUnsaved}
                                      onChange={(e) =>
                                        isUnsaved &&
                                        updateEntry(
                                          entryNumber,
                                          index,
                                          "note_line",
                                          e.target.value
                                        )
                                      }
                                      className="bg-black/40 text-white"
                                      placeholder={isUnsaved ? "Enter note line" : ""}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {entries.length > 1 && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeRowFromEntry(entryNumber, index)}
                                        className="text-red-400 hover:bg-red-500/10"
                                        disabled={isArchivalMode}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )})}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

